/**
 * IconContextMenu - right-click context menu for desktop/dock icons
 */

"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useThemeOptional } from "../contexts/ThemeContext";

export interface IconContextMenuProps {
  /** Screen position to render the menu */
  position: { x: number; y: number };
  /** Whether the icon is currently in the dock */
  isInDock: boolean;
  /** Whether the current layout mode is "dock" (context menu only shows in dock mode) */
  isDockMode: boolean;
  /** Whether the dock is at its maximum capacity */
  dockFull?: boolean;
  /** Whether this icon can be hidden (pinned icons cannot) */
  canHide?: boolean;
  /** Whether the icon's window is currently open or minimized (shows Quit option) */
  isRunning?: boolean;
  /** Called when user selects "Move to Dock" */
  onMoveToDock: () => void;
  /** Called when user selects "Move to Desktop" */
  onMoveToDesktop: () => void;
  /** Called when user selects "Hide from Dock" */
  onHide?: () => void;
  /** Called when user selects "Quit" — closes the running window */
  onQuit?: () => void;
  /** Called when the menu should close */
  onClose: () => void;
}

export function IconContextMenu({
  position,
  isInDock,
  isDockMode,
  dockFull,
  canHide = true,
  isRunning = false,
  onMoveToDock,
  onMoveToDesktop,
  onHide,
  onQuit,
  onClose,
}: IconContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const [adjustedPos, setAdjustedPos] = useState(position);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    // Delay adding listener to avoid immediate close from the contextmenu event
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleEscape);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Measure the menu after render and clamp to viewport
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = position.x;
    let y = position.y;

    // If menu overflows right edge, flip to the left of the cursor
    if (x + rect.width > vw) {
      x = Math.max(0, vw - rect.width - 4);
    }
    // If menu overflows bottom, position it above the click point
    if (y + rect.height > vh) {
      y = Math.max(0, y - rect.height);
    }

    if (x !== position.x || y !== position.y) {
      setAdjustedPos({ x, y });
    }
  }, [position]);

  const menuStyle: React.CSSProperties = {
    position: "fixed",
    left: adjustedPos.x,
    top: adjustedPos.y,
    zIndex: 10000,
    minWidth: 180,
  };

  const handleMoveToDock = useCallback(() => {
    onMoveToDock();
    onClose();
  }, [onMoveToDock, onClose]);

  const handleMoveToDesktop = useCallback(() => {
    onMoveToDesktop();
    onClose();
  }, [onMoveToDesktop, onClose]);

  const handleHide = useCallback(() => {
    onHide?.();
    onClose();
  }, [onHide, onClose]);

  const handleQuit = useCallback(() => {
    onQuit?.();
    onClose();
  }, [onQuit, onClose]);

  const showQuit = isRunning && !!onQuit;

  // Only show move options in dock layout mode
  if (!isDockMode) return null;

  return (
    <div
      ref={menuRef}
      style={menuStyle}
      className="overflow-hidden rounded-lg border shadow-2xl"
      role="menu"
    >
      <div
        className="py-1"
        style={{
          backgroundColor: isDark
            ? "rgba(40, 40, 45, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.15)",
        }}
      >
        {isInDock ? (
          <button
            className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-blue-500 hover:text-white"
            style={{
              color: isDark ? "#e4e4e7" : "#27272a",
            }}
            onClick={handleMoveToDesktop}
            role="menuitem"
          >
            Move to Desktop
          </button>
        ) : (
          <button
            className={`w-full px-4 py-2 text-left text-sm transition-colors ${dockFull ? "cursor-default" : "hover:bg-blue-500 hover:text-white"}`}
            style={{
              color: dockFull
                ? isDark
                  ? "rgba(228, 228, 231, 0.35)"
                  : "rgba(39, 39, 42, 0.35)"
                : isDark
                  ? "#e4e4e7"
                  : "#27272a",
            }}
            onClick={dockFull ? undefined : handleMoveToDock}
            role="menuitem"
            aria-disabled={dockFull}
          >
            {dockFull ? "Move to Dock (Full)" : "Move to Dock"}
          </button>
        )}
        {canHide && onHide && (
          <>
            <div
              style={{
                height: 1,
                margin: "2px 8px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.08)",
              }}
            />
            <button
              className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-blue-500 hover:text-white"
              style={{
                color: isDark ? "#e4e4e7" : "#27272a",
              }}
              onClick={handleHide}
              role="menuitem"
            >
              Hide
            </button>
          </>
        )}
        {showQuit && (
          <>
            <div
              style={{
                height: 1,
                margin: "2px 8px",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.08)",
              }}
            />
            <button
              className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-red-500 hover:text-white"
              style={{
                color: isDark ? "#e4e4e7" : "#27272a",
              }}
              onClick={handleQuit}
              role="menuitem"
            >
              Quit
            </button>
          </>
        )}
      </div>
    </div>
  );
}
